export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  QUICK_START: '/quick-start',
  // Every setup page as one grid of cards
  SETUP: '/setup',
  PROFILE: '/profile',
  // Platform screens: the lists that work without a Rental
  RENTALS: '/rentals',
  // Platform screens: the global vehicle model templates (admin in Global view)
  GLOBAL_VEHICLE_MODELS: '/global-vehicle-models',
  GLOBAL_VEHICLE_MODELS_CREATE: '/global-vehicle-models/create',
  GLOBAL_VEHICLE_MODELS_EDIT: '/global-vehicle-models/edit/:id',
  GLOBAL_VEHICLE_MODELS_SHOW: '/global-vehicle-models/show/:id',
  SECURITY_EVENTS: '/security-events',
  // Platform screens: every user, across rentals
  ADMIN_USERS: '/admin-users',
  ADMIN_USERS_SHOW: '/admin-users/show/:id',

  // Users of the Rental (Manager, or an Admin impersonating)
  USERS: '/users',
  USERS_SHOW: '/users/show/:id',

  // Customers
  CUSTOMERS: '/customers',
  CUSTOMERS_CREATE: '/customers/create',
  CUSTOMERS_EDIT: '/customers/edit/:id',
  CUSTOMERS_SHOW: '/customers/show/:id',

  // Vehicle Groups (catalog categories)
  VEHICLE_GROUPS: '/vehicle-groups',
  VEHICLE_GROUPS_CREATE: '/vehicle-groups/create',
  VEHICLE_GROUPS_EDIT: '/vehicle-groups/edit/:id',
  VEHICLE_GROUPS_SHOW: '/vehicle-groups/show/:id',
  // Every pricing rule of one group, seasons as columns
  VEHICLE_GROUPS_PRICING: '/vehicle-groups/:id/pricing',

  // Vehicle Models (catalog items)
  VEHICLE_MODELS: '/vehicle-models',
  VEHICLE_MODELS_CREATE: '/vehicle-models/create',
  VEHICLE_MODELS_EDIT: '/vehicle-models/edit/:id',
  VEHICLE_MODELS_SHOW: '/vehicle-models/show/:id',

  // Vehicles (physical instances)
  VEHICLES: '/vehicles',
  VEHICLES_CREATE: '/vehicles/create',
  VEHICLES_EDIT: '/vehicles/edit/:id',
  VEHICLES_SHOW: '/vehicles/show/:id',

  // Stations
  STATIONS: '/stations',
  STATIONS_CREATE: '/stations/create',
  STATIONS_EDIT: '/stations/edit/:id',
  STATIONS_SHOW: '/stations/show/:id',
  STATIONS_FEES: '/stations/:id/fees',

  // Reservations
  RESERVATIONS: '/reservations',
  RESERVATIONS_CREATE: '/reservations/create',
  RESERVATIONS_EDIT: '/reservations/edit/:id',
  RESERVATIONS_SHOW: '/reservations/show/:id',

  // Extras (global add-ons)
  EXTRAS: '/extras',
  EXTRAS_CREATE: '/extras/create',
  EXTRAS_EDIT: '/extras/edit/:id',
  EXTRAS_SHOW: '/extras/show/:id',
  EXTRAS_FEES: '/extras/:id/fees',

  // Insurances (tenant-scoped insurance options)
  INSURANCES: '/insurances',
  INSURANCES_CREATE: '/insurances/create',
  INSURANCES_EDIT: '/insurances/edit/:id',
  INSURANCES_SHOW: '/insurances/show/:id',
  INSURANCES_FEES: '/insurances/:id/fees',

  // Seasons (recurring yearly date periods)
  SEASONS: '/seasons',
  SEASONS_CREATE: '/seasons/create',
  SEASONS_EDIT: '/seasons/edit/:id',
  SEASONS_SHOW: '/seasons/show/:id',
  SEASONS_FEES: '/seasons/:id/fees',

  // Policies
  OFFICE_HOURS: '/policies/opening-hours',
  OFFICE_HOURS_EDIT: '/policies/opening-hours/:stationId/edit',
  // Temporary: the v2 overview and station page, until they replace OFFICE_HOURS(_EDIT).
  OPENING_HOURS_V2: '/policies/opening-hours-v2',
  OPENING_HOURS_V2_STATION: '/policies/opening-hours-v2/:stationId',
  EXTRA_DRIVERS: '/policies/extra-drivers',
  DRIVER_AGE: '/policies/driver-age',
  ROAD_ASSISTANCE: '/policies/road-assistance',
  KM_POLICIES: '/policies/km-limits',
  RENTAL_DURATION: '/policies/rental-duration',
  DISCOUNTS: '/discounts',

  // Tenant settings: pricing at /settings. The Rental profile is its own top-level
  // Sider item, so its route sits outside /settings.
  SETTINGS: '/settings',
  RENTAL_PROFILE: '/rental-profile',

  // Calendar
  CALENDAR: '/calendar',
  DAILY_RATES: '/daily-rates',

  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  LOGOUT: '/logout',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  // A password someone else set must be changed before anything else works
  CHANGE_PASSWORD: '/change-password',
} as const;

// Helper function to generate edit/show routes
export const generateRoute = {
  customerEdit: (id: string): string => `/customers/edit/${id}`,
  customerShow: (id: string): string => `/customers/show/${id}`,
  vehicleGroupEdit: (id: string): string => `/vehicle-groups/edit/${id}`,
  vehicleGroupShow: (id: string): string => `/vehicle-groups/show/${id}`,
  vehicleGroupPricing: (id: string): string => `/vehicle-groups/${id}/pricing`,
  vehicleModelEdit: (id: string): string => `/vehicle-models/edit/${id}`,
  vehicleModelShow: (id: string): string => `/vehicle-models/show/${id}`,
  globalVehicleModelEdit: (id: string): string => `/global-vehicle-models/edit/${id}`,
  globalVehicleModelShow: (id: string): string => `/global-vehicle-models/show/${id}`,
  vehicleEdit: (id: string): string => `/vehicles/edit/${id}`,
  vehicleShow: (id: string): string => `/vehicles/show/${id}`,
  stationEdit: (id: string): string => `/stations/edit/${id}`,
  stationShow: (id: string): string => `/stations/show/${id}`,
  stationFees: (id: string): string => `/stations/${id}/fees`,
  reservationEdit: (id: string): string => `/reservations/edit/${id}`,
  reservationShow: (id: string): string => `/reservations/show/${id}`,
  extraEdit: (id: string): string => `/extras/edit/${id}`,
  extraShow: (id: string): string => `/extras/show/${id}`,
  extraFees: (id: string): string => `/extras/${id}/fees`,
  insuranceEdit: (id: string): string => `/insurances/edit/${id}`,
  insuranceShow: (id: string): string => `/insurances/show/${id}`,
  insuranceFees: (id: string): string => `/insurances/${id}/fees`,
  seasonEdit: (id: string): string => `/seasons/edit/${id}`,
  seasonShow: (id: string): string => `/seasons/show/${id}`,
  seasonFees: (id: string): string => `/seasons/${id}/fees`,
  userShow: (id: string): string => `/users/show/${id}`,
  adminUserShow: (id: string): string => `/admin-users/show/${id}`,
  officeHoursEdit: (stationId: string): string => `/policies/opening-hours/${stationId}/edit`,
  openingHoursV2Station: (stationId: string): string => `/policies/opening-hours-v2/${stationId}`,
};

// `?reason=` value on /login when the app signed the user out because their
// session ended (a refresh token revoked or expired). Login shows a notice for it.
export const SESSION_ENDED_REASON = 'session-ended';
